/**
 * Blog Content Reader Utilities
 * 
 * A comprehensive utility module for handling blog content reading,
 * view tracking, read time calculation, and content processing.
 * 
 * This module can be used in Next.js projects to manage blog content
 * reading functionality consistently across the application.
 * 
 * Usage:
 *   import { calculateReadTime, incrementViewCount, formatBlogDate } from '@/lib/utils/blog-content-reader';
 */

import type { Collection, ObjectId } from 'mongodb';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Blog post interface (minimal required fields)
 */
export interface BlogPost {
  _id?: ObjectId | string;
  content?: string;
  views?: number | null;
  publishDate?: Date | string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  [key: string]: unknown;
}

/**
 * Enriched blog post with calculated fields
 */
export interface EnrichedBlogPost extends BlogPost {
  readTime: number;
  formattedDate: string;
  _id: string;
  views: number;
}

/**
 * View increment options
 */
export interface ViewIncrementOptions {
  skipIncrement?: boolean;
  collection: Collection<BlogPost>;
  blogId: ObjectId | string;
  currentViews?: number | null;
}

/**
 * Read time calculation options
 */
export interface ReadTimeOptions {
  wordsPerMinute?: number;
  minReadTime?: number;
}

/**
 * Date formatting options
 */
export interface DateFormatOptions {
  locale?: string;
  year?: 'numeric' | '2-digit';
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
}

// ============================================================================
// WORD COUNT & TEXT EXTRACTION
// ============================================================================

/**
 * Extracts plain text from HTML content.
 * Removes HTML tags, scripts, styles, and normalizes whitespace.
 * 
 * @param html - The HTML string to extract text from
 * @returns Plain text content
 * 
 * @example
 * extractTextFromHTML('<p>Hello <strong>World</strong>!</p>') // 'Hello World!'
 */
export function extractTextFromHTML(html: string): string {
  if (!html || typeof html !== 'string') return '';

  if (typeof window !== 'undefined') {
    // Browser environment - use DOM parsing
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Remove script and style elements
    const scripts = tempDiv.querySelectorAll('script, style');
    scripts.forEach(el => el.remove());

    // Get text content and clean it up
    let text = tempDiv.textContent || tempDiv.innerText || '';
    // Clean up extra whitespace
    text = text.replace(/\s+/g, ' ').trim();
    return text;
  } else {
    // Server environment - use regex (less accurate but works)
    let text = html.replace(/<[^>]*>/g, ' '); // Remove HTML tags
    text = text.replace(/\s+/g, ' ').trim(); // Normalize whitespace
    return text;
  }
}

/**
 * Counts the number of words in a text string.
 * 
 * @param text - The text to count words in
 * @returns The number of words
 * 
 * @example
 * countWords('Hello world, this is a test!') // 6
 * countWords('') // 0
 */
export function countWords(text: string): number {
  if (!text || typeof text !== 'string') return 0;
  return text.trim().split(/\s+/).filter(word => word.length > 0).length;
}

/**
 * Counts words in HTML content by extracting text first.
 * 
 * @param html - The HTML content to count words in
 * @returns The number of words
 * 
 * @example
 * countWordsInHTML('<p>Hello <strong>World</strong>!</p>') // 2
 */
export function countWordsInHTML(html: string): number {
  const text = extractTextFromHTML(html);
  return countWords(text);
}

// ============================================================================
// READ TIME CALCULATION
// ============================================================================

/**
 * Calculates the estimated read time for content based on word count.
 * Default: 200 words per minute (average reading speed).
 * 
 * @param content - The content (HTML or plain text)
 * @param options - Calculation options
 * @returns Estimated read time in minutes (minimum 1 minute)
 * 
 * @example
 * calculateReadTime('This is a blog post with many words...') // 1 (minimum)
 * calculateReadTime(htmlContent, { wordsPerMinute: 150 }) // Custom reading speed
 */
export function calculateReadTime(
  content: string,
  options: ReadTimeOptions = {}
): number {
  const {
    wordsPerMinute = 200, // Average reading speed
    minReadTime = 1 // Minimum read time in minutes
  } = options;

  if (!content) return minReadTime;

  // Count words (handles both HTML and plain text)
  const wordCount = content.includes('<')
    ? countWordsInHTML(content)
    : countWords(content);

  // Calculate read time
  const readTime = Math.ceil(wordCount / wordsPerMinute);

  // Return minimum read time if calculated time is less
  return Math.max(minReadTime, readTime);
}

/**
 * Calculates read time from word count directly.
 * 
 * @param wordCount - The number of words
 * @param options - Calculation options
 * @returns Estimated read time in minutes
 */
export function calculateReadTimeFromWordCount(
  wordCount: number,
  options: ReadTimeOptions = {}
): number {
  const {
    wordsPerMinute = 200,
    minReadTime = 1
  } = options;

  if (wordCount <= 0) return minReadTime;

  const readTime = Math.ceil(wordCount / wordsPerMinute);
  return Math.max(minReadTime, readTime);
}

// ============================================================================
// VIEW COUNT MANAGEMENT
// ============================================================================

/**
 * Increments the view count for a blog post.
 * Handles null/undefined views by defaulting to 0.
 * 
 * @param options - View increment options
 * @returns Promise that resolves when view count is updated
 * 
 * @example
 * await incrementViewCount({
 *   collection: blogsCollection,
 *   blogId: blog._id,
 *   currentViews: blog.views
 * });
 */
export async function incrementViewCount(
  options: ViewIncrementOptions
): Promise<void> {
  const {
    skipIncrement = false,
    collection,
    blogId,
    currentViews
  } = options;

  // Skip if explicitly requested (e.g., for metadata/server-side fetches)
  if (skipIncrement) {
    return;
  }

  // Ensure views is always a number (convert null/undefined to 0 and increment)
  const newViews = (currentViews || 0) + 1;

  await collection.updateOne(
    { _id: blogId },
    {
      $set: {
        views: newViews
      }
    }
  );
}

/**
 * Checks if view count should be incremented based on session storage.
 * Prevents duplicate view counts on page refresh.
 * 
 * @param slug - The blog slug
 * @param viewIncrementedRef - React ref to track if already incremented
 * @returns True if view should be incremented
 * 
 * @example
 * const viewIncrementedRef = useRef(false);
 * const shouldIncrement = shouldIncrementView(slug, viewIncrementedRef);
 */
export function shouldIncrementView(
  slug: string,
  viewIncrementedRef?: { current: boolean }
): boolean {
  if (typeof window === 'undefined') return false;

  // Check ref if provided
  if (viewIncrementedRef?.current) return false;

  // Check session storage
  const viewKey = `view_incremented_${slug}`;
  const alreadyIncremented = sessionStorage.getItem(viewKey);

  return !alreadyIncremented;
}

/**
 * Marks a view as incremented in session storage.
 * 
 * @param slug - The blog slug
 * @param viewIncrementedRef - Optional React ref to update
 * 
 * @example
 * markViewAsIncremented(slug, viewIncrementedRef);
 */
export function markViewAsIncremented(
  slug: string,
  viewIncrementedRef?: { current: boolean }
): void {
  if (typeof window === 'undefined') return;

  const viewKey = `view_incremented_${slug}`;
  sessionStorage.setItem(viewKey, 'true');

  if (viewIncrementedRef) {
    viewIncrementedRef.current = true;
  }
}

/**
 * Gets the view increment parameter for API calls.
 * 
 * @param shouldIncrement - Whether to increment views
 * @returns Query parameter string
 * 
 * @example
 * const param = getViewIncrementParam(shouldIncrement);
 * // Returns: '' or '?skipIncrement=true'
 */
export function getViewIncrementParam(shouldIncrement: boolean): string {
  return shouldIncrement ? '' : '?skipIncrement=true';
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Formats a date for display in blog posts.
 * 
 * @param date - The date to format (Date, string, or undefined)
 * @param options - Date formatting options
 * @returns Formatted date string or empty string if date is invalid
 * 
 * @example
 * formatBlogDate(new Date()) // 'January 1, 2024'
 * formatBlogDate('2024-01-01', { locale: 'en-GB' }) // '1 January 2024'
 */
export function formatBlogDate(
  date: Date | string | undefined | null,
  options: DateFormatOptions = {}
): string {
  if (!date) return '';

  const {
    locale = 'en-US',
    year = 'numeric',
    month = 'long',
    day = 'numeric'
  } = options;

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toLocaleDateString(locale, {
      year,
      month,
      day
    });
  } catch {
    return '';
  }
}

/**
 * Formats date in a short format (e.g., "Jan 1, 2024").
 * 
 * @param date - The date to format
 * @returns Short formatted date string
 */
export function formatBlogDateShort(
  date: Date | string | undefined | null
): string {
  return formatBlogDate(date, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Formats date in ISO format (e.g., "2024-01-01").
 * 
 * @param date - The date to format
 * @returns ISO formatted date string
 */
export function formatBlogDateISO(
  date: Date | string | undefined | null
): string {
  if (!date) return '';

  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;

    if (isNaN(dateObj.getTime())) {
      return '';
    }

    return dateObj.toISOString().split('T')[0];
  } catch {
    return '';
  }
}

// ============================================================================
// BLOG DATA ENRICHMENT
// ============================================================================

/**
 * Enriches a blog post with calculated fields (readTime, formattedDate, etc.).
 * 
 * @param blog - The blog post to enrich
 * @param options - Enrichment options
 * @returns Enriched blog post with all calculated fields
 * 
 * @example
 * const enriched = enrichBlogPost(blog, {
 *   wordsPerMinute: 200,
 *   locale: 'en-US'
 * });
 */
export function enrichBlogPost(
  blog: BlogPost,
  options: {
    readTimeOptions?: ReadTimeOptions;
    dateFormatOptions?: DateFormatOptions;
  } = {}
): EnrichedBlogPost {
  const {
    readTimeOptions = {},
    dateFormatOptions = {}
  } = options;

  // Calculate read time
  const readTime = blog.content
    ? calculateReadTime(blog.content, readTimeOptions)
    : 1;

  // Format date
  const formattedDate = formatBlogDate(blog.publishDate || blog.createdAt, dateFormatOptions);

  // Convert ObjectId to string
  const id = blog._id?.toString() || '';

  // Ensure views is a number
  const views = blog.views || 0;

  return {
    ...blog,
    _id: id,
    readTime,
    formattedDate,
    views
  } as EnrichedBlogPost;
}

/**
 * Enriches multiple blog posts at once.
 * 
 * @param blogs - Array of blog posts to enrich
 * @param options - Enrichment options
 * @returns Array of enriched blog posts
 */
export function enrichBlogPosts(
  blogs: BlogPost[],
  options: {
    readTimeOptions?: ReadTimeOptions;
    dateFormatOptions?: DateFormatOptions;
  } = {}
): EnrichedBlogPost[] {
  return blogs.map(blog => enrichBlogPost(blog, options));
}

// ============================================================================
// CONTENT FETCHING HELPERS
// ============================================================================

/**
 * Fetches a blog post with view tracking.
 * Handles view increment logic and session storage.
 * 
 * @param slug - The blog slug
 * @param apiUrl - The API endpoint URL
 * @param options - Fetch options
 * @returns Promise resolving to blog data
 * 
 * @example
 * const blog = await fetchBlogWithViews('my-blog-post', '/api/blogs', {
 *   viewIncrementedRef,
 *   skipIncrement: false
 * });
 */
export async function fetchBlogWithViews(
  slug: string,
  apiUrl: string,
  options: {
    viewIncrementedRef?: { current: boolean };
    skipIncrement?: boolean;
  } = {}
): Promise<{ blog: EnrichedBlogPost } | null> {
  const { viewIncrementedRef, skipIncrement = false } = options;

  // Determine if we should increment views
  let shouldIncrement = !skipIncrement;
  if (!skipIncrement && viewIncrementedRef) {
    shouldIncrement = shouldIncrementView(slug, viewIncrementedRef);
  }

  // Build API URL with increment parameter
  const incrementParam = getViewIncrementParam(shouldIncrement);
  const url = `${apiUrl}/${encodeURIComponent(slug)}${incrementParam}`;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Blog not found');
      }
      throw new Error('Failed to fetch blog');
    }

    const data = await response.json();

    // Mark view as incremented if we incremented it
    if (shouldIncrement) {
      markViewAsIncremented(slug, viewIncrementedRef);
    }

    return data;
  } catch (error) {
    console.error('Error fetching blog:', error);
    throw error;
  }
}

// ============================================================================
// CONTENT PROCESSING
// ============================================================================

/**
 * Strips HTML tags from content (simple regex-based approach).
 * Use extractTextFromHTML for more accurate results in browser.
 * 
 * @param html - HTML content
 * @returns Plain text
 */
export function stripHTML(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * Gets a preview/summary of content (first N characters).
 * 
 * @param content - The content (HTML or plain text)
 * @param maxLength - Maximum length of preview
 * @param stripHTML - Whether to strip HTML tags
 * @returns Preview text
 * 
 * @example
 * getContentPreview('<p>Long blog post content...</p>', 100) // 'Long blog post content...'
 */
export function getContentPreview(
  content: string,
  maxLength = 150,
  stripHTMLTags = true
): string {
  if (!content) return '';

  const text = stripHTMLTags ? extractTextFromHTML(content) : content;

  if (text.length <= maxLength) {
    return text;
  }

  // Truncate and add ellipsis
  return text.substring(0, maxLength).trim() + '...';
}

/**
 * Validates content length for processing (e.g., AI summarization).
 * 
 * @param content - The content to validate
 * @param maxWords - Maximum allowed words
 * @returns Validation result
 * 
 * @example
 * const validation = validateContentLength(htmlContent, 10000);
 * if (!validation.isValid) {
 *   console.error(validation.error);
 * }
 */
export function validateContentLength(
  content: string,
  maxWords: number
): { isValid: boolean; wordCount: number; error?: string } {
  const wordCount = countWordsInHTML(content);

  if (wordCount > maxWords) {
    return {
      isValid: false,
      wordCount,
      error: `Content exceeds maximum size of ${maxWords} words. Current: ${wordCount} words`
    };
  }

  return {
    isValid: true,
    wordCount
  };
}

// ============================================================================
// REACT HOOKS (Client-side only)
// ============================================================================

/**
 * React hook for managing blog view tracking.
 * Note: This requires React to be imported in your component file.
 * 
 * @param slug - The blog slug
 * @returns Object with view tracking state and functions
 * 
 * @example
 * import { useRef } from 'react';
 * const { shouldIncrement, markAsIncremented, viewIncrementedRef } = useBlogViewTracking('my-blog');
 */
export function useBlogViewTracking(slug: string) {
  // Note: This function returns utilities but doesn't use React hooks directly
  // to avoid requiring React as a dependency. Use useRef in your component instead.
  const viewIncrementedRef = { current: false };

  const shouldIncrement = shouldIncrementView(slug, viewIncrementedRef);

  const markAsIncremented = () => {
    markViewAsIncremented(slug, viewIncrementedRef);
  };

  return {
    shouldIncrement,
    markAsIncremented,
    viewIncrementedRef
  };
}

// ============================================================================
// EXPORT ALL UTILITIES
// ============================================================================

const blogContentReaderUtils = {
  // Text Extraction & Word Counting
  extractTextFromHTML,
  countWords,
  countWordsInHTML,
  stripHTML,

  // Read Time Calculation
  calculateReadTime,
  calculateReadTimeFromWordCount,

  // View Count Management
  incrementViewCount,
  shouldIncrementView,
  markViewAsIncremented,
  getViewIncrementParam,
  useBlogViewTracking,

  // Date Formatting
  formatBlogDate,
  formatBlogDateShort,
  formatBlogDateISO,

  // Blog Data Enrichment
  enrichBlogPost,
  enrichBlogPosts,

  // Content Fetching
  fetchBlogWithViews,

  // Content Processing
  getContentPreview,
  validateContentLength
};

export default blogContentReaderUtils;

